import React from "react";
import { FaUserTie } from "react-icons/fa";
import CustomInput from "../../common/CustomInput";
import CustomDropdown from "../../common/CustomDropdown";
import PhoneNumberField from "../common/PhoneNumberField";
import { COACH_ROLE_OPTIONS } from "../../../constants/staffRoles";

const CoachDetailsSection = ({
  isEditing,
  isSaving,
  onEdit,
  onCancel,
  onSaveClick,
  formData,
  handleInputChange,
  onDocumentUpload,
  isLoadingDocuments,
  onOpenMobileModal,
  onOpenImageUpload,
  onOpenChangePassword,
  countryOptions = [],
  dateOfBirthError,
}) => {
  // Coaching titles come from the platform's canonical role list
  // (constants/staffRoles.js, mirroring Staff.ROLE_TYPE_CHOICES). The list
  // already arrives grouped — Head Coach titles first, then the Co Trainer /
  // Assistant Coach ones — and that order is kept as-is.
  const positionOptions = COACH_ROLE_OPTIONS;

  return (
    <div className="bg-white p-6 border border-[#E6E6E6]">
      <div className="flex justify-between items-center gap-3 mb-6 flex-col sm:flex-row">
        <h2 className="text-[11px] font-bold text-[#3C3C3C] self-start">COACH DETAILS</h2>
        <div className="self-end">
          {!isEditing ? (
            <div className="flex items-center gap-2">
              <button
                onClick={onOpenChangePassword}
                className="border border-[#E6E6E6] px-4 py-2 rounded-lg text-[#1E1E1E]"
              >
                Change password
              </button>
              <button
                onClick={onEdit}
                className="bg-[#2283FF] px-4 py-2 text-[#F5F5F5] rounded-lg"
              >
                Edit profile
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={onCancel}
                disabled={isSaving}
                className="border border-[#E6E6E6] px-4 py-2 rounded-lg text-[#1E1E1E]"
              >
                Cancel
              </button>
              <button
                onClick={onSaveClick}
                disabled={isSaving}
                className="bg-[#2283FF] px-4 py-2 text-[#F5F5F5] rounded-lg"
              >
                {isSaving ? "Saving..." : "Save changes"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Profile Picture */}
      <div className="flex items-center gap-6 mb-6">
        <div className="flex-shrink-0 relative">
          <div
            className="w-24 h-24 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden cursor-pointer"
            onClick={() => isEditing && onOpenImageUpload()}
          >
            {formData.profileImage ? (
              <img src={formData.profileImage} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <FaUserTie className="text-3xl text-gray-600" />
            )}
          </div>
          <div className="absolute bottom-0 right-0 w-6 h-6 bg-[#2283FF] rounded-full flex items-center justify-center">
            <span className="text-white text-xs font-bold">TP</span>
          </div>
        </div>
      </div>

      {/* Row 1: Name Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <CustomInput
          label="First name"
          value={formData.firstName}
          onChange={(e) => handleInputChange("firstName", e.target.value)}
          placeholder="Enter first name"
          disabled={true}
        />
        <CustomInput
          label="Last name"
          value={formData.lastName}
          onChange={(e) => handleInputChange("lastName", e.target.value)}
          placeholder="Enter last name"
          disabled={true}
        />
      </div>

      {/* Row 2: Position and Current Club */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Position is user-generated content like every other field on this
            page. Left empty it still shows what the sports data knows. */}
        <div className="mb-4">
          <label className="block text-sm font-bold text-[#1A212F] mb-2">Position</label>
          <CustomDropdown
            options={positionOptions}
            value={formData.selfPosition || ""}
            onChange={(value) => handleInputChange("selfPosition", value)}
            placeholder={formData.position || "Select your position (optional)"}
            disabled={!isEditing}
            searchEnabled
            searchPlaceholder="Search position..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Club
          </label>
          <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
            {/* NOTE: this is the coach's NATIONALITY flag, sitting in the
                Current Club box — pre-existing, left untouched on purpose.
                Reported separately; not this package's to change. */}
            {formData.nationalityLogo && (
              <img
                src={formData.nationalityLogo}
                alt={formData.nationality}
                className="w-5 h-4 object-cover"
              />
            )}
            <span className="text-sm text-[#1A212F] font-medium">
              {formData.clubDetails?.name || 'Not specified'}
            </span>
          </div>
        </div>
      </div>

      {/* Row 2b: Date of birth and Nationality — both optional, both
          user-generated. Empty is a valid, complete profile; the sports data
          then answers, and when it cannot, nothing is shown at all. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <CustomInput
          label="Date of birth"
          type="date"
          value={formData.dateOfBirth || ""}
          onChange={(e) => handleInputChange("dateOfBirth", e.target.value)}
          placeholder="Optional"
          disabled={!isEditing}
          error={dateOfBirthError}
          max={new Date().toISOString().split("T")[0]}
        />
        <div className="mb-4">
          <label className="block text-sm font-bold text-[#1A212F] mb-2">Nationality</label>
          <CustomDropdown
            options={countryOptions}
            value={formData.selfNationality || ""}
            onChange={(value) => handleInputChange("selfNationality", value)}
            placeholder={formData.nationality || "Select your nationality (optional)"}
            disabled={!isEditing}
            searchEnabled
            searchPlaceholder="Search country..."
          />
        </div>
      </div>

      {/* Row 3: Email Address and Phone Number */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <CustomInput
          label="Email address"
          value={formData.email}
          onChange={(e) => handleInputChange("email", e.target.value)}
          placeholder="Enter email address"
          type="email"
          required
          disabled={true}
        />
        <PhoneNumberField
          label="Phone number"
          value={formData.mobile}
          disabled
          showEdit={isEditing}
          onEdit={onOpenMobileModal}
        />
      </div>

      {/* Row 3: Specialization */}
      {formData.specialization && (
        <div className="mb-4">
          <CustomInput
            label="Specialization"
            value={formData.specialization}
            onChange={(e) => handleInputChange("specialization", e.target.value)}
            placeholder="Enter specialization"
            disabled={!isEditing}
          />
        </div>
      )}

      {/* Resume Upload */}
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">CV / Resume</label>
        {formData.resumeUrl || formData.resumeFile ? (
          <div className="flex items-center gap-3 p-3 bg-[#F8F9FB] border border-gray-300 rounded-md">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 2H4C3.44772 2 3 2.44772 3 3V17C3 17.5523 3.44772 18 4 18H16C16.5523 18 17 17.5523 17 17V7L11 2H8Z" stroke="#006BF3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M11 2V7H17" stroke="#006BF3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="text-sm text-gray-700">
              {formData.resumeFile?.name || 'Resume.pdf'}
            </span>
            {formData.resumeUrl && !formData.resumeFile && (
              <a 
                href={formData.resumeUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="ml-auto text-blue-600 hover:text-blue-800 text-sm"
              >
                View
              </a>
            )}
            {isEditing && (
              <button
                onClick={() => handleInputChange("resumeFile", null)}
                className="ml-auto text-red-500 hover:text-red-700"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center w-full h-20 border-2 border-dashed border-gray-300 rounded-md bg-[#F8F9FB]">
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={async (e) => {
                const file = e.target.files && e.target.files[0];
                if (file && onDocumentUpload) {
                  try {
                    await onDocumentUpload('resume', file.name, file);
                    handleInputChange("resumeFile", file);
                  } catch (error) {
                    console.error('Resume upload failed:', error);
                  }
                }
              }}
              className="hidden"
              id="resume-upload"
              disabled={!isEditing || isLoadingDocuments}
            />
            <label
              htmlFor="resume-upload"
              className={`cursor-pointer text-sm text-gray-500 ${(!isEditing || isLoadingDocuments) ? 'cursor-not-allowed opacity-50' : ''}`}
            >
              {isLoadingDocuments ? "Uploading..." : isEditing ? "Click to upload CV/Resume" : "No file uploaded"}
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoachDetailsSection;

